import { i18n } from '@/core/i18n'
import { computed } from 'vue'
import type { IconName } from '@easybell/caramel-vue3'

const linkPattern = computed(() => {
  return /^.*(\[(.*)\]\((https?:\/\/.*)\)).*$/
})

function subsectionTitle(item: string) {
  return i18n.global.t(item).match(linkPattern.value)?.[2] ?? ''
}

function subsectionPath(item: string) {
  return i18n.global.t(item).match(linkPattern.value)?.[3] ?? ''
}

export const supportingText = computed(() => {
  return (
    i18n.global.t('footer.supportingText1') +
    '\n' +
    i18n.global.t('footer.supportingText2')
  )
})

export const socialMedia = computed(() => {
  return [
    { icon: 'Facebook' as IconName, link: 'https://www.facebook.com/easybell' },
    {
      icon: 'Youtube' as IconName,
      link: 'https://www.youtube.com/easybell_gmbh',
    },
    {
      icon: 'LinkedIn' as IconName,
      link: 'https://www.linkedin.com/company/easybell-gmbh',
    },
    {
      icon: 'Github' as IconName,
      link: 'https://github.com/easybell-gmbh',
    },
  ]
})

export const newsletter = computed(() => {
  return {
    title: i18n.global.t('footer.contact.title'),
    body: i18n.global.t('footer.contact.context'),
    button: {
      label: subsectionTitle('footer.button.contact'),
      link: subsectionPath('footer.button.contact'),
    },
  }
})

export const navigationMenus = computed(() => {
  return [
    {
      isOpen: true,
      label: i18n.global.t('footer.pages.title.myProduct'),
      childrens: [
        {
          label: computed(() =>
            subsectionTitle('footer.pages.myProduct.myContract')
          ).value,
          link: computed(() =>
            subsectionPath('footer.pages.myProduct.myContract')
          ).value,
        },
        {
          label: computed(() =>
            subsectionTitle('footer.pages.myProduct.subscriptionManagement')
          ).value,
          link: computed(() =>
            subsectionPath('footer.pages.myProduct.subscriptionManagement')
          ).value,
        },
        {
          label: computed(() =>
            subsectionTitle('footer.pages.myProduct.premiumService')
          ).value,
          link: computed(() =>
            subsectionPath('footer.pages.myProduct.premiumService')
          ).value,
        },
        {
          label: computed(() =>
            subsectionTitle('footer.pages.myProduct.userManagement')
          ).value,
          link: computed(() =>
            subsectionPath('footer.pages.myProduct.userManagement')
          ).value,
        },
        {
          label: computed(() =>
            subsectionTitle('footer.pages.myProduct.subsidiaryManagement')
          ).value,
          link: computed(() =>
            subsectionPath('footer.pages.myProduct.subsidiaryManagement')
          ).value,
        },
        {
          label: computed(() =>
            subsectionTitle('footer.pages.myProduct.internetForEmployees')
          ).value,
          link: computed(() =>
            subsectionPath('footer.pages.myProduct.internetForEmployees')
          ).value,
        },
      ],
    },
    {
      isOpen: true,
      label: i18n.global.t('footer.pages.title.myData'),
      childrens: [
        {
          label: computed(() => subsectionTitle('footer.pages.myData.bills'))
            .value,
          link: computed(() => subsectionPath('footer.pages.myData.bills'))
            .value,
        },
        {
          label: computed(() =>
            subsectionTitle('footer.pages.myData.callHistory')
          ).value,
          link: computed(() =>
            subsectionPath('footer.pages.myData.callHistory')
          ).value,
        },
      ],
    },
    {
      isOpen: true,
      label: i18n.global.t('footer.pages.title.phoneSettings'),
      childrens: [
        {
          label: computed(() =>
            subsectionTitle('footer.pages.phoneSettings.phoneNumbers')
          ).value,
          link: computed(() =>
            subsectionPath('footer.pages.phoneSettings.phoneNumbers')
          ).value,
        },
        {
          label: computed(() =>
            subsectionTitle('footer.pages.phoneSettings.teamsConnector')
          ).value,
          link: computed(() =>
            subsectionPath('footer.pages.phoneSettings.teamsConnector')
          ).value,
        },
        {
          label: computed(() =>
            subsectionTitle('footer.pages.phoneSettings.cloudTelephoneSystem')
          ).value,
          link: computed(() =>
            subsectionPath('footer.pages.phoneSettings.cloudTelephoneSystem')
          ).value,
        },
        {
          label: computed(() =>
            subsectionTitle('footer.pages.phoneSettings.contacts')
          ).value,
          link: computed(() =>
            subsectionPath('footer.pages.phoneSettings.contacts')
          ).value,
        },
        {
          label: computed(() =>
            subsectionTitle('footer.pages.phoneSettings.sendFax')
          ).value,
          link: computed(() =>
            subsectionPath('footer.pages.phoneSettings.sendFax')
          ).value,
        },
        {
          label: computed(() =>
            subsectionTitle('footer.pages.phoneSettings.sendSms')
          ).value,
          link: computed(() =>
            subsectionPath('footer.pages.phoneSettings.sendSms')
          ).value,
        },
      ],
    },
    {
      isOpen: true,
      label: i18n.global.t('footer.pages.title.help'),
      childrens: [
        {
          label: computed(() => subsectionTitle('footer.pages.help.contact'))
            .value,
          link: computed(() => subsectionPath('footer.pages.help.contact'))
            .value,
        },
        {
          label: computed(() => subsectionTitle('footer.pages.help.helpCenter'))
            .value,
          link: computed(() => subsectionPath('footer.pages.help.helpCenter'))
            .value,
        },
        {
          label: computed(() => subsectionTitle('footer.pages.help.speedCheck'))
            .value,
          link: computed(() => subsectionPath('footer.pages.help.speedCheck'))
            .value,
        },
      ],
    },
  ]
})

export const copyright = computed(() => {
  return i18n.global.t('footer.copyright')
})

export const selectOptions = computed(() => {
  return [
    {
      label: i18n.global.t('global.common.language.german'),
      value: 'de' as const,
    },
    {
      label: i18n.global.t('global.common.language.english'),
      value: 'en' as const,
    },
    {
      label: i18n.global.t('global.common.language.french'),
      value: 'fr' as const,
    },
    {
      label: i18n.global.t('global.common.language.dutch'),
      value: 'nl' as const,
    },
  ]
}) as any // TODO Design shared library doesn't support nl - for build I need to set this to any

export const locale = computed(() => {
  return i18n.global.locale.value as any // TODO Design shared library doesn't support nl - for build I need to set this to any
})
